import { useEffect, useMemo, useState } from "react";
import { fetchAccount } from "./api.js";
import { AccountChip } from "./AccountChip.jsx";
import { RecordingsPanel } from "./RecordingsPanel.jsx";
import { isNativeMeetHost } from "./auth.js";
import { MeetAvatar } from "./VideoTile.jsx";
import { shortId, shortName } from "./format.js";
import stageMark from "./icons/icon-a-stage.svg";

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function clock(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function dayBucket(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { key: 0, label: "Unknown date" };
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const diff = Math.round((todayStart - start) / 86400000);
  if (diff === 0) return { key: start, label: `Today · ${date}` };
  if (diff === 1) return { key: start, label: `Yesterday · ${date}` };
  return { key: start, label: date };
}

function viaLabel(via) {
  if (via === "cli") return "Beagle CLI";
  if (via === "wallet") return "wallet app";
  if (via === "launch") return "Beagle Apps";
  if (via === "web") return "app.beagle.chat";
  return via || "Beagle";
}

function outageNote(room) {
  const raw = room?.outageDuration;
  if (raw == null || raw === "") return "";
  if (typeof raw === "number" && Number.isFinite(raw)) return `down ${raw} min`;
  const text = String(raw).trim();
  return text;
}

function roomsFrom(data) {
  if (Array.isArray(data?.rooms)) return data.rooms;
  const owned = data?.owned || [];
  const meetings = data?.meetings || [];
  const byId = new Map();
  for (const room of owned) {
    const id = room._id || room.id;
    if (!id) continue;
    byId.set(id, {
      id,
      name: room.name || id,
      role: "host",
      visits: 0,
      lastVisit: null,
      live: false,
      upSince: null,
      lastOutage: null,
      outageDuration: null,
      description: room.description || "",
      icon: room.icon || "",
      requireMic: room.requireMic !== false,
      engine: room.engine || "licode",
    });
  }
  for (const meeting of meetings) {
    const id = meeting.roomId;
    if (!id) continue;
    const existing = byId.get(id);
    if (existing) {
      existing.visits = meeting.visits || 0;
      existing.lastVisit = meeting.lastAt || null;
      continue;
    }
    byId.set(id, {
      id,
      name: meeting.name || id,
      role: "joined",
      visits: meeting.visits || 0,
      lastVisit: meeting.lastAt || null,
      live: false,
      upSince: null,
      lastOutage: null,
      outageDuration: null,
      description: "",
      icon: "",
      requireMic: true,
      engine: "licode",
    });
  }
  return [...byId.values()];
}

function historyFrom(data) {
  if (Array.isArray(data?.history)) return data.history;
  return (data?.attendance || []).map((event) => {
    const type = String(event.type || "");
    const kind = type === "enter" ? "join" : type === "exit" ? "exit" : type === "reenter" ? "recover" : type;
    const verb = kind === "join" ? "Joined" : kind === "exit" ? "Left" : kind === "recover" ? "Rejoined" : type;
    return {
      ts: event.at,
      kind,
      cat: "visit",
      text: verb,
      source: [event.client, event.platform].filter(Boolean).join(" "),
      roomId: event.roomId,
    };
  });
}

function CopyVal({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span>—</span>;
  return (
    <span title={value} className="bm-copyline">
      <span>{shortId(value)}</span>
      <button
        type="button"
        className="bm-copy"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 900);
          } catch {
            /* ignore */
          }
        }}
      >
        {copied ? "copied" : "copy"}
      </button>
    </span>
  );
}

function asJoinRoom(room) {
  return {
    ...room,
    _id: room._id || room.id,
    isOwner: room.role === "host" || !!room.isOwner,
  };
}

export function AccountProfile({
  apiBase,
  identity,
  canCreate = false,
  canHost = false,
  joiningId,
  onCreate,
  onJoin,
  onUpdate,
  onClose,
  onOpenHost,
  onOpenRoomLog,
  onSignOut,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [section, setSection] = useState("rooms");
  const [roomFilter, setRoomFilter] = useState("all");
  const [histFilter, setHistFilter] = useState("all");
  const [recCount, setRecCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [requireMic, setRequireMic] = useState(true);
  const [engine, setEngine] = useState("licode");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [copied, setCopied] = useState("");
  const native = isNativeMeetHost();

  const reload = () =>
    fetchAccount(apiBase)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load account"));

  useEffect(() => {
    let cancelled = false;
    fetchAccount(apiBase)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load account");
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  const profile = data || identity || {};
  const allowCreate = !!(data?.canCreate ?? canCreate);
  const via = profile.via || identity?.via || "";
  const displayName = profile.name || shortName(profile.userid || profile.userId) || "You";
  const rooms = useMemo(() => roomsFrom(data), [data]);
  const history = useMemo(() => historyFrom(data), [data]);
  const chat = native ? { via, links: [], note: "Chat is in the Beagle app on this device." } : data?.chat;
  const hostedCount = rooms.filter((r) => r.role === "host").length;
  const joinedCount = rooms.filter((r) => r.role === "joined").length;
  const shownRooms = rooms.filter((r) => {
    if (roomFilter === "host") return r.role === "host";
    if (roomFilter === "joined") return r.role === "joined";
    return true;
  });
  const shownHistory = history.filter((row) => {
    if (histFilter === "visit") return row.cat === "visit";
    if (histFilter === "room") return row.cat === "room";
    return true;
  });
  const histGroups = [];
  for (const row of shownHistory) {
    const bucket = dayBucket(row.ts);
    let group = histGroups.find((g) => g.key === bucket.key);
    if (!group) {
      group = { key: bucket.key, label: bucket.label, rows: [] };
      histGroups.push(group);
    }
    group.rows.push(row);
  }
  const link = native ? null : chat?.links?.[0] || null;

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(id);
      setTimeout(() => setCopied(""), 900);
    } catch {
      /* ignore */
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onCreate?.({
        name: name.trim(),
        requireMic,
        description: description.trim(),
        icon: icon.trim(),
        engine,
      });
      setName("");
      setDescription("");
      setIcon("");
      setRequireMic(true);
      setEngine("licode");
      setCreating(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const categories = [
    ["profile", "Profile", null],
    ["rooms", "Rooms", rooms.length],
    ["recordings", "Recordings", recCount],
    ["history", "History", history.length],
  ];

  return (
    <div className="bm-acct-page">
      <header className="bm-acct-top">
        <button type="button" className="bm-acct-back" onClick={onClose} aria-label="Back to meetings">←</button>
        <div className="bm-acct-crumb">
          <img className="bm-mark" src={stageMark} alt="" width="28" height="28" />
          <button type="button" className="bm-acct-crumb-link" onClick={onClose}>Meetings</button>
          <span className="bm-acct-slash">/</span>
          <span>Account</span>
        </div>
        <h1 className="bm-acct-mtitle">Account</h1>
        <AccountChip
          identity={profile.userid || profile.userId ? profile : identity}
          canHost={canHost || allowCreate}
          chat={chat}
          onOpenAccount={() => setSection("profile")}
          onOpenHost={onOpenHost}
          onSignOut={onSignOut}
        />
      </header>

      {error ? <div className="bm-err">{error}</div> : null}

      <div className="bm-acct-body">
        <aside className="bm-acct-side">
          <div className="bm-acct-who">
            <MeetAvatar identity={profile} size={44} />
            <div>
              <div className="bm-acct-name">{displayName}</div>
              <div className="bm-acct-sub">Signed in via {viaLabel(via)}</div>
            </div>
          </div>
          <nav className="bm-acct-nav">
            {categories.map(([id, label, count]) => (
              <button key={id} type="button" className={section === id ? "on" : ""} onClick={() => setSection(id)}>
                <span>{label}</span>
                {count == null ? <em /> : <em>{count}</em>}
              </button>
            ))}
          </nav>
          <div className="bm-acct-side-foot">
            {link ? (
              <a className="bm-acct-chat" href={link.href} target="_blank" rel="noreferrer">
                {link.label || "Open Beagle chat"} ↗
                {link.hint ? <small>{link.hint}</small> : null}
              </a>
            ) : (
              <p className="bm-acct-note">{chat?.note || ""}</p>
            )}
            <p className="bm-acct-keys">We never hold private keys or passwords.</p>
          </div>
        </aside>

        <div className="bm-acct-main">
          <div className="bm-acct-mcard">
            <MeetAvatar identity={profile} size={52} />
            <div className="bm-acct-mcard-text">
              <div className="bm-acct-name">{displayName}</div>
              <div className="bm-acct-sub">Signed in via {viaLabel(via)}</div>
              <div className="bm-acct-sub bm-acct-id">{shortId(profile.userid || profile.userId)}</div>
            </div>
            <button type="button" className="bm-join sec" onClick={() => setSection("profile")}>Details</button>
          </div>
          <div className="bm-acct-tabs" role="tablist">
            {[
              ["rooms", "Rooms"],
              ["recordings", "Recordings"],
              ["history", "History"],
            ].map(([id, label]) => (
              <button key={id} type="button" className={section === id ? "on" : ""} onClick={() => setSection(id)}>
                {label}
              </button>
            ))}
          </div>

          {section === "profile" ? (
            <section>
              <div className="bm-acct-sec-h">
                <div>
                  <h2>Profile</h2>
                  <p>Identity stored when you signed in. We never hold private keys or passwords.</p>
                </div>
              </div>
              <div className="bm-acct-who bm-acct-hero">
                <MeetAvatar identity={profile} size={72} />
                <div>
                  <div className="bm-acct-name">{displayName}</div>
                  {profile.description ? <p className="bm-acct-bio">{profile.description}</p> : null}
                </div>
              </div>
              <dl className="bm-acct-rows">
                <div><dt>Session userid</dt><dd><CopyVal value={profile.userid || profile.userId} /></dd></div>
                <div><dt>Beagle userid</dt><dd><CopyVal value={profile.decentUserid} /></dd></div>
                <div><dt>Carrier address</dt><dd><CopyVal value={profile.address} /></dd></div>
                <div><dt>Punk</dt><dd>{profile.punkId ?? "—"}</dd></div>
                <div><dt>First seen</dt><dd>{when(profile.firstSeenAt) || "—"}</dd></div>
                <div>
                  <dt>Last seen</dt>
                  <dd>
                    {when(profile.lastSeen?.at) || "—"} {profile.lastSeen?.client || ""} {profile.lastSeen?.platform || ""}
                  </dd>
                </div>
              </dl>
            </section>
          ) : null}

          {section === "rooms" ? (
            <section>
              <div className="bm-acct-sec-h">
                <div>
                  <h2>Rooms</h2>
                  <p>Rooms you host and rooms you have joined.</p>
                </div>
                {allowCreate ? (
                  <button type="button" className="bm-create bm-create-desk" onClick={() => setCreating(true)}>
                    <span style={{ fontSize: 18, fontWeight: 400 }}>+</span>Create room
                  </button>
                ) : null}
              </div>
              <div className="bm-seg" role="tablist">
                {[
                  ["all", `All ${rooms.length}`],
                  ["host", `Hosted ${hostedCount}`],
                  ["joined", `Joined ${joinedCount}`],
                ].map(([id, label]) => (
                  <button key={id} type="button" className={roomFilter === id ? "on" : ""} onClick={() => setRoomFilter(id)}>
                    {label}
                  </button>
                ))}
              </div>
              {!data && !error ? <div className="bm-empty">Loading…</div> : null}
              {data && shownRooms.length === 0 ? (
                <div className="bm-empty">
                  {allowCreate && roomFilter !== "joined"
                    ? "You do not host a room yet. Create one here."
                    : "No rooms in this list."}
                </div>
              ) : null}
              <div className="bm-acct-rooms">
                {shownRooms.map((room) => {
                  const id = room.id || room._id;
                  const note = outageNote(room);
                  const up = when(room.upSince);
                  const outage = when(room.lastOutage);
                  return (
                    <article key={id} className="bm-acct-room">
                      <div className="bm-acct-room-h">
                        <div>
                          <div className="bm-acct-room-title">
                            {room.icon ? <img className="bm-room-icon" src={room.icon} alt="" width="28" height="28" /> : null}
                            <span className="bm-name">{room.name}</span>
                            <span className={room.role === "host" ? "bm-badge-host" : "bm-badge-joined"}>
                              {room.role === "host" ? "HOST" : "JOINED"}
                            </span>
                            {room.live ? (
                              <span className="bm-live"><i />LIVE</span>
                            ) : (
                              <span className="bm-badge-idle">IDLE</span>
                            )}
                          </div>
                          <div className="bm-idrow">
                            <div className="bm-id" title={id}>{id}</div>
                            <button type="button" className="bm-copy" onClick={() => copyId(id)}>
                              {copied === id ? "copied" : "copy"}
                            </button>
                          </div>
                          {room.description ? <div className="bm-room-desc">{room.description}</div> : null}
                        </div>
                        <div className="bm-acct-room-actions">
                          {room.role === "host" || allowCreate ? (
                            <button type="button" className="bm-host-link" onClick={() => onOpenRoomLog?.(asJoinRoom(room))}>
                              Room log
                            </button>
                          ) : null}
                          {room.role === "host" ? (
                            <button
                              type="button"
                              className="bm-host-link"
                              onClick={() => {
                                setEditingId(editingId === id ? "" : id);
                                setEditDescription(room.description || "");
                                setEditIcon(room.icon || "");
                              }}
                            >
                              {editingId === id ? "Cancel" : "Edit"}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="bm-join"
                            disabled={!!joiningId}
                            onClick={() => onJoin?.(asJoinRoom(room))}
                          >
                            {joiningId === id ? "Joining…" : "Join"}
                          </button>
                        </div>
                      </div>
                      {editingId === id ? (
                        <form
                          className="bm-create-extra"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            if (busy) return;
                            setBusy(true);
                            try {
                              await onUpdate?.(asJoinRoom(room), {
                                description: editDescription.trim(),
                                icon: editIcon.trim(),
                                requireMic: room.requireMic !== false,
                              });
                              setEditingId("");
                              await reload();
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          <div className="bm-search">
                            <input value={editIcon} onChange={(e) => setEditIcon(e.target.value)} placeholder="Icon URL (https)" />
                          </div>
                          <textarea
                            className="bm-desc-input"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            placeholder="Description"
                            rows={3}
                          />
                          <button type="submit" className="bm-join sec" disabled={busy}>
                            {busy ? "Saving…" : "Save"}
                          </button>
                        </form>
                      ) : null}
                      <div className="bm-room-stats">
                        <div><span>Your visits</span><strong>{room.visits || 0}</strong></div>
                        <div><span>Last visit</span><strong>{when(room.lastVisit) || "—"}</strong></div>
                        <div><span>Up since (last restart)</span><strong className={up ? "" : "muted"}>{up || "None"}</strong></div>
                        <div>
                          <span>Last outage</span>
                          {outage ? (
                            <strong className="warn">{outage}{note ? ` · ${note}` : ""}</strong>
                          ) : (
                            <strong className="muted">None</strong>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
                {allowCreate ? (
                  <button type="button" className="bm-create-dash" onClick={() => setCreating(true)}>+ Create room</button>
                ) : null}
              </div>
            </section>
          ) : null}

          <section hidden={section !== "recordings"}>
            <div className="bm-acct-sec-h">
              <div>
                <h2>Recordings</h2>
                <p>Audio, camera, and screen. Each published stream is its own file.</p>
              </div>
            </div>
            <RecordingsPanel apiBase={apiBase} onCount={setRecCount} />
          </section>

          {section === "history" ? (
            <section>
              <div className="bm-acct-sec-h">
                <div>
                  <h2>History</h2>
                  <p>Your visits, plus room restarts and outages when the server has recorded them.</p>
                </div>
              </div>
              <div className="bm-seg" role="tablist">
                {[
                  ["all", "All"],
                  ["visit", "My visits"],
                  ["room", "Room events"],
                ].map(([id, label]) => (
                  <button key={id} type="button" className={histFilter === id ? "on" : ""} onClick={() => setHistFilter(id)}>
                    {label}
                  </button>
                ))}
              </div>
              {!data && !error ? <div className="bm-empty">Loading…</div> : null}
              {data && shownHistory.length === 0 ? (
                <div className="bm-empty">
                  {histFilter === "room"
                    ? "No room events yet. Restarts and outages show up here when the server records them."
                    : "No history yet."}
                </div>
              ) : null}
              {histGroups.map((group) => (
                <div key={group.key}>
                  <div className="bm-hist-day">{group.label}</div>
                  {group.rows.map((row, i) => (
                    <div key={`${row.ts}-${row.roomId || ""}-${i}`} className="bm-hist-row">
                      <span className="bm-hist-time">{clock(row.ts) || "—"}</span>
                      <span className={`bm-kind ${row.kind || "join"}`}>{row.kind === "backUp" ? "back up" : row.kind || "event"}</span>
                      <span>{row.text}</span>
                      <span className="bm-hist-src">{row.source || ""}</span>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          ) : null}
        </div>
      </div>

      {creating && allowCreate ? (
        <div className="bm-modal-back" onClick={() => !busy && setCreating(false)}>
          <form className="bm-modal" onClick={(e) => e.stopPropagation()} onSubmit={submitCreate}>
            <h3>Create room</h3>
            <div className="bm-search">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Room name" autoFocus />
            </div>
            <label className="bm-check">
              <input type="checkbox" checked={requireMic} onChange={(e) => setRequireMic(e.target.checked)} />
              Require microphone to join
            </label>
            {import.meta.env.DEV ? (
              <label className="bm-check">
                Media
                <select value={engine} onChange={(e) => setEngine(e.target.value)}>
                  <option value="licode">Licode</option>
                  <option value="agora">Agora</option>
                </select>
              </label>
            ) : null}
            <div className="bm-search">
              <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="Icon URL (https)" />
            </div>
            <textarea
              className="bm-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              rows={3}
            />
            <div className="bm-modal-actions">
              <button type="button" className="bm-join sec" onClick={() => setCreating(false)} disabled={busy}>Cancel</button>
              <button type="submit" className="bm-join" disabled={busy || !name.trim()}>{busy ? "Creating…" : "Create"}</button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
